import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers, FormikValues, useField } from "formik";
import * as yup from "yup";
import { useBoundStore } from "../../../store";
import { BANNED_EMAIL_DOMAINS } from "./constants";
import axiosInstant from "../../../api";
import { getCheckoutSession, registrationEndpoint } from "../../../api/endpoints";
import { SystemRoles } from "../../../providers/roles";
import { handoffHostname, readRegisterHandoff } from "../../../utils/funnelHandoff";
import { TermsConsentText } from "../../../components/TermsConsentText/TermsConsentText";
import { GoogleButton } from "../../../components/auth/GoogleButton";
import { googleErrorCode, googleMessage, googleRegisterBrand } from "../../../components/auth/googleSignIn";
import { rememberSignInMethod } from "../../../utils/lastSignInMethod";

const FONT = "Lato, sans-serif";
const COLOR_BRAND_RED = "#EB3E33";
const COLOR_WARNING = "#ffb74d";

const labelSx = {
    color: "rgba(255,255,255,0.7)",
    fontFamily: FONT,
    fontWeight: 300,
    fontSize: "12px",
    lineHeight: "100%",
    letterSpacing: 0,
    marginBottom: "6px"
};

const inputSx = {
    "& .MuiInputBase-root": {
        color: "#fff",
        fontFamily: FONT,
        fontSize: "12px",
        fontWeight: 300,
        lineHeight: "100%",
        "&:before": { borderBottomColor: "rgba(255,255,255,0.4)" },
        "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: "rgba(255,255,255,0.7)"
        },
        "&:after": { borderBottomColor: "#fff" },
        "&.Mui-disabled:before": {
            borderBottomStyle: "solid",
            borderBottomColor: "rgba(255,255,255,0.2)"
        }
    },
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "rgba(255,255,255,0.5)"
    },
    "& .MuiFormHelperText-root": {
        color: COLOR_BRAND_RED,
        marginLeft: 0,
        fontFamily: FONT,
        fontSize: "12px"
    }
};

const bodyTextSx = {
    color: "rgba(255,255,255,0.8)",
    fontFamily: FONT,
    fontWeight: 300,
    fontSize: "12px",
    lineHeight: "1.4",
    letterSpacing: 0
};

const submitButtonStyle: React.CSSProperties = {
    backgroundColor: "#1B202C",
    fontFamily: FONT,
    fontWeight: 400,
    fontStyle: "italic",
    fontSize: "14px",
    lineHeight: "100%",
    letterSpacing: 0
};

interface DarkLabelProps {
    children: React.ReactNode;
    required?: boolean;
}

function DarkLabel({ children, required }: DarkLabelProps) {
    return (
        <Typography sx={labelSx}>
            {children}
            {required && <span style={{ marginLeft: 2 }}>*</span>}
        </Typography>
    );
}

interface DarkFieldProps {
    name: string;
    label: string;
    type?: string;
    disabled?: boolean;
    required?: boolean;
    onChangeExtra?: (value: string) => void;
}

function DarkField({ name, label, type = "text", disabled, required, onChangeExtra }: DarkFieldProps) {
    const [field, meta, helpers] = useField(name);
    const showError = Boolean(meta.touched && meta.error);
    return (
        <Box className="flex flex-col w-full">
            <DarkLabel required={required}>{label}</DarkLabel>
            <TextField
                {...field}
                type={type}
                variant="standard"
                fullWidth
                disabled={disabled}
                error={showError}
                helperText={showError ? meta.error : undefined}
                sx={inputSx}
                onChange={(e) => {
                    helpers.setValue(e.target.value);
                    if (onChangeExtra) onChangeExtra(e.target.value);
                }}
            />
        </Box>
    );
}

interface DarkCheckboxProps {
    name: string;
    label: React.ReactNode;
}

function DarkCheckbox({ name, label }: DarkCheckboxProps) {
    const [field, meta, helpers] = useField(name);
    const showError = Boolean(meta.touched && meta.error);
    return (
        <Box>
            <Box className="flex items-start gap-3">
                <Checkbox
                    checked={field.value}
                    onChange={(e) => helpers.setValue(e.target.checked)}
                    sx={{
                        color: "rgba(255,255,255,0.5)",
                        padding: 0,
                        marginTop: "2px",
                        "&.Mui-checked": { color: "#fff" }
                    }}
                />
                <Typography sx={bodyTextSx}>{label}</Typography>
            </Box>
            {showError && (
                <Typography
                    sx={{
                        color: COLOR_BRAND_RED,
                        fontFamily: FONT,
                        fontSize: "12px",
                        mt: 1
                    }}
                >
                    {meta.error}
                </Typography>
            )}
        </Box>
    );
}

interface InfoMessageProps {
    children: React.ReactNode;
}

function InfoMessage({ children }: InfoMessageProps) {
    return (
        <Typography
            sx={{
                color: COLOR_WARNING,
                fontFamily: FONT,
                fontSize: "13px",
                marginTop: "16px",
                lineHeight: 1.5
            }}
        >
            {children}
        </Typography>
    );
}

interface DarkSubmitButtonProps {
    loading?: boolean;
    children: React.ReactNode;
}

function DarkSubmitButton({ loading, children }: DarkSubmitButtonProps) {
    return (
        <button
            id="brand-registration-button"
            type="submit"
            disabled={loading}
            className="mt-2 w-full rounded-full py-4 text-white transition-opacity disabled:opacity-60 text-center"
            style={submitButtonStyle}
        >
            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : children}
        </button>
    );
}

interface StripePrefilledFields {
    email: string;
    firstName: string;
    lastName: string;
}

// `onFailed` (Google mode only) hears about a lookup that didn't load; the
// password form passes none and keeps ignoring failures as before.
function useStripeCheckoutPrefill(apply: (fields: StripePrefilledFields) => void, onFailed?: () => void) {
    const [searchParams] = useSearchParams();
    const applyRef = useRef(apply);
    applyRef.current = apply;
    const onFailedRef = useRef(onFailed);
    onFailedRef.current = onFailed;

    useEffect(() => {
        const checkoutSessionId = searchParams.get("checkout_session_id");
        if (!checkoutSessionId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const stripeResponse: any = await axiosInstant.get(getCheckoutSession(checkoutSessionId));
                if (cancelled) return;
                const { name, email } = stripeResponse.data.customer_details || {};
                const parts = (name || "").trim().split(/\s+/);
                applyRef.current({
                    email: email || "",
                    firstName: parts[0] || "",
                    lastName: parts.slice(1).join(" ") || ""
                });
            } catch {
                if (!cancelled && onFailedRef.current) onFailedRef.current();
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchParams]);
}

// Set by GoogleReturnPage when a "Continue with Google" registration comes
// back to this form: the verified email (locked here; the API takes it from
// its own session, never from the body) and the names Google supplied.
interface GoogleRegistration {
    flowId: string;
    email: string;
    firstName: string;
    lastName: string;
}

const sameEmail = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirmation: "",
    brand: "",
    website: "",
    termsCheckbox: false
};

const registrationSchema = yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup
        .string()
        .email("Enter a valid email")
        .required("Email is required")
        .test("banned-domain", "Please use a business email, not a public domain like @gmail.com.", (value) => {
            if (!value) return true;
            const domain = value.split("@")[1]?.toLowerCase();
            return !BANNED_EMAIL_DOMAINS.includes(domain);
        }),
    password: yup.string().required("Password is required"),
    passwordConfirmation: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("password"), null], "Passwords must match"),
    brand: yup.string().required("Brand name is required"),
    website: yup
        .string()
        .required("Brand website is required")
        .matches(/^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i, "Enter your brand's website, e.g. yourbrand.com"),
    termsCheckbox: yup.bool().oneOf([true], "You must agree to the terms to continue")
});

// The Google path has no password; everything else is validated as before.
const googleRegistrationSchema = registrationSchema.omit(["password", "passwordConfirmation"]);

export function RegisterForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const { brand } = useParams();
    const login = useBoundStore((state) => state.login);
    const autoLogin = useBoundStore((state) => state.autoLogin);
    const [searchParams] = useSearchParams();
    // Funnel handoff (TDC-1573): the audit / strategy pages link here with
    // the lead's email + audited product; the API claims those at register.
    const handoff = useMemo(() => readRegisterHandoff(searchParams), [searchParams]);
    const handoffHost = handoffHostname(handoff.funnel?.pdp_url);
    // Google mode: back from "Sign up with Google" with the verified identity
    // held in the API's session. The email is locked and there is no password.
    const google = (location.state as { google?: GoogleRegistration } | null)?.google;
    // Self-registration always creates a new brand; the API rejects an
    // existing brand or an already-imported funnel product (joining a brand
    // is by team invite).
    const [existsError, setExistsError] = useState<"brand" | "product" | null>(null);
    const [prefilledValues, setPrefilledValues] = useState({
        ...initialValues,
        email: google ? google.email : handoff.email,
        firstName: google?.firstName || "",
        lastName: google?.lastName || "",
        brand: brand || ""
    });
    // The purchase email on a checkout registration. On the Google path a
    // different Google email would leave the purchase disconnected from the
    // account, so that combination can't be submitted.
    const [checkoutEmail, setCheckoutEmail] = useState<string | null>(null);
    // Google mode can't tell a match from a mismatch until the purchase
    // email is known, so it waits for it, and stops if it can't be loaded.
    const isCheckout = !!searchParams.get("checkout_session_id");
    const [checkoutLookup, setCheckoutLookup] = useState<"loading" | "loaded" | "failed">("loading");

    useStripeCheckoutPrefill(
        (fields) => {
            setCheckoutEmail(fields.email || null);
            setCheckoutLookup(fields.email ? "loaded" : "failed");
            // Google's email and names win over the Stripe prefill.
            setPrefilledValues((prev) => (google ? prev : { ...prev, ...fields }));
        },
        () => setCheckoutLookup("failed")
    );

    const checkoutMismatch = !!google && !!checkoutEmail && !sameEmail(checkoutEmail, google.email);
    const checkoutPending = !!google && isCheckout && checkoutLookup === "loading";
    const checkoutUnknown = !!google && isCheckout && checkoutLookup === "failed";
    const checkoutBlocked = checkoutMismatch || checkoutPending || checkoutUnknown;
    // Back to the same register page (keeps /register/:brand and the query)
    // as a full page load, not an in-app switch: the form starts fresh, so
    // the purchase or audit email fills in again exactly as on a direct
    // visit. Browsers keep a page's history state across a same-address
    // reload, so the Google state is cleared from it first.
    const passwordFormPath = `${location.pathname}${location.search}`;
    const openPasswordForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        window.history.replaceState(null, "", passwordFormPath);
        window.location.reload();
    };
    const funnelMismatch = !!google && !!handoff.email && !sameEmail(handoff.email, google.email);

    const handleSubmit = async (values: FormikValues, helpers: FormikHelpers<typeof initialValues>) => {
        helpers.setSubmitting(true);
        setExistsError(null);

        try {
            let registered: { first_product_queued?: boolean } | undefined;
            if (google) {
                if (checkoutBlocked) {
                    helpers.setSubmitting(false);
                    return;
                }
                const signedIn = await googleRegisterBrand(google.flowId, {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    brand: values.brand,
                    website: values.website,
                    ...(handoff.funnel ? { funnel: handoff.funnel } : {})
                });
                registered = signedIn;
                await autoLogin({ tokens: signedIn.tokens });
                rememberSignInMethod("google");
            } else {
                const response = await axiosInstant.post(registrationEndpoint(), {
                    ...values,
                    role: SystemRoles.PRO,
                    ...(handoff.funnel ? { funnel: handoff.funnel } : {})
                });
                registered = response.data;
                await login(values.email, values.password);
            }
            // With a product import queued, land on the catalog: the slug-free
            // path is rewritten to /portal/:slug/products (PortalRedirect
            // keeps the query) and ?welcome=1 polls for the import. The API
            // skips it when the catalog already has that product.
            navigate(registered?.first_product_queued ? "/products?welcome=1" : "/");
        } catch (e) {
            const error = e as AxiosError;
            const errorMessage = error.response?.data;
            if (errorMessage) {
                // Coded errors arrive as a string, or { error, ...details }.
                const code = typeof errorMessage === "string" ? errorMessage : (errorMessage as any).error;
                if (code === "BrandAlreadyExists") {
                    setExistsError("brand");
                } else if (code === "ProductAlreadyExists") {
                    setExistsError("product");
                } else if (code === "EmailAlreadyExists") {
                    helpers.setErrors({ email: "Email already exists" });
                } else if (code === "WebsiteDomainNotAllowed") {
                    helpers.setErrors({
                        website: "Use your brand's own website, not a retailer, marketplace or social page."
                    });
                } else if (code === "WebsiteRequired" || code === "InvalidWebsite") {
                    helpers.setErrors({ website: "Enter your brand's website, e.g. yourbrand.com" });
                } else if (google) {
                    // Google-flow codes (expired session, stale tab, …) get a
                    // fixed message; nothing from the response is shown raw.
                    helpers.setErrors({ termsCheckbox: googleMessage(googleErrorCode(e)) });
                } else {
                    helpers.setErrors({ termsCheckbox: code as string });
                }
            }
        }
        helpers.setSubmitting(false);
    };

    return (
        <Formik
            initialValues={prefilledValues}
            enableReinitialize
            validationSchema={google ? googleRegistrationSchema : registrationSchema}
            validateOnChange={false}
            validateOnBlur
            onSubmit={handleSubmit}
        >
            {({ isSubmitting }) => (
                <Form className="w-full">
                    <Stack spacing={2.5} width={1}>
                        {!google && (
                            <GoogleButton
                                intent="brand"
                                dark
                                loginHint={checkoutEmail || handoff.email || undefined}
                            />
                        )}

                        {google && (
                            <InfoMessage>
                                Signing up with Google as {google.email}.{" "}
                                <a href={passwordFormPath} onClick={openPasswordForm} style={{ color: "#fff", textDecoration: "underline" }}>
                                    Use email and password instead
                                </a>
                                .
                            </InfoMessage>
                        )}

                        <DarkField name="email" label="Email" type="email" required disabled={!!google} />

                        {!google && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DarkField name="password" label="Password" type="password" required />
                                <DarkField name="passwordConfirmation" label="Confirm Password" type="password" required />
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <DarkField name="firstName" label="First Name" required />
                            <DarkField name="lastName" label="Last Name" required />
                        </div>

                        <DarkField
                            name="brand"
                            label="Brand Name"
                            disabled={!!brand}
                            required
                            onChangeExtra={() => setExistsError(null)}
                        />

                        <DarkField name="website" label="Brand Website" required />

                        <DarkCheckbox name="termsCheckbox" label={<TermsConsentText />} />

                        {existsError && (
                            <InfoMessage>
                                {existsError === "brand"
                                    ? "This brand already exists. Ask your team admin for an invite, or "
                                    : "This product already exists on The Desire Company. Please "}
                                <a
                                    href="mailto:help@thedesirecompany.com"
                                    style={{ color: "#fff", textDecoration: "underline" }}
                                >
                                    contact us
                                </a>
                                .
                            </InfoMessage>
                        )}

                        {checkoutMismatch && (
                            <InfoMessage>
                                This purchase was made with {checkoutEmail}, but you signed in to Google as{" "}
                                {google?.email}. To keep the purchase with your account, continue with Google as{" "}
                                {checkoutEmail}, or{" "}
                                <a href={passwordFormPath} onClick={openPasswordForm} style={{ color: "#fff", textDecoration: "underline" }}>
                                    register with email and password
                                </a>
                                .
                            </InfoMessage>
                        )}
                        {checkoutMismatch && (
                            <GoogleButton intent="brand" dark loginHint={checkoutEmail || undefined} label="Continue with Google as the purchase email" />
                        )}

                        {checkoutPending && <InfoMessage>Checking your purchase details…</InfoMessage>}
                        {checkoutUnknown && (
                            <InfoMessage>
                                We couldn&apos;t load your purchase details, so we can&apos;t match this purchase to your Google
                                account.{" "}
                                <a href={passwordFormPath} onClick={openPasswordForm} style={{ color: "#fff", textDecoration: "underline" }}>
                                    Register with email and password
                                </a>{" "}
                                instead.
                            </InfoMessage>
                        )}

                        {!checkoutMismatch && funnelMismatch && (
                            <InfoMessage>
                                Your earlier results were run as {handoff.email}. They may not carry over to an account
                                registered as {google?.email}.
                            </InfoMessage>
                        )}

                        {!checkoutMismatch && !checkoutUnknown && (
                            <DarkSubmitButton loading={isSubmitting || checkoutPending}>Create My Account</DarkSubmitButton>
                        )}

                        {handoffHost && (
                            <Typography
                                sx={{
                                    color: "rgba(255,255,255,0.7)",
                                    fontFamily: FONT,
                                    fontSize: "12px",
                                    fontWeight: 300,
                                    textAlign: "center"
                                }}
                            >
                                We&apos;ll add your product on {handoffHost} to your catalog and score it weekly
                                for free.
                            </Typography>
                        )}
                    </Stack>
                </Form>
            )}
        </Formik>
    );
}
